"use client";

import { FC, useRef } from "react";
import Draggable from "react-draggable";

import { cn } from "@/lib/utils";
import { APP_CONFIG } from "@/config";

interface EnvironmentIndicatorProps {
  className?: string;
}

/**
 * Displays current runtime environment.
 * - Hidden automatically in production
 * - Visible in dev / staging / uat
 */
export const EnvironmentIndicator: FC<EnvironmentIndicatorProps> = ({
  className,
}) => {
  const env = APP_CONFIG.environment?.toLowerCase();
  const nodeRef = useRef<HTMLDivElement | null>(null);

  // 🚫 Never show in production
  if (env === "production") return null;

  const ENV_UI: Record<
    string,
    { label: string; className: string }
  > = {
    development: {
      label: "Development",
      className: "bg-emerald-600",
    },
    staging: {
      label: "Staging",
      className: "bg-amber-600",
    },
    uat: {
      label: "UAT",
      className: "bg-indigo-600",
    },
    test: {
      label: "Test",
      className: "bg-sky-600",
    },
  };

  const fallback = {
    label: "Unknown Environment",
    className: "bg-destructive",
  };

  const ui = ENV_UI[env ?? ""] ?? fallback;

  return (
    <Draggable
      nodeRef={nodeRef}
      axis="x"
      bounds="body"
      defaultPosition={{ x: 0, y: 0 }}
    >
      <div
        ref={nodeRef}
        className={cn(
          "fixed top-0 left-1/2 -translate-x-1/2 z-[999]",
          "px-3 py-1 text-xs font-semibold tracking-wide text-white",
          "rounded-b-lg shadow-md select-none opacity-90",
          "cursor-move",
          ui.className,
          className
        )}
        title="Application Environment"
      >
        {ui.label} Environment
      </div>
    </Draggable>
  );
};
